import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  }, []);

  // Global interceptor: auto-logout on 401/403 (suspended or deleted user)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error.response?.status;
        const detail = error.response?.data?.detail || "";
        if (status === 403 && detail.toLowerCase().includes("suspended")) {
          toast.error("Your account has been suspended. Contact support.");
          logout();
        } else if (status === 401 && detail.toLowerCase().includes("not found")) {
          toast.error("Your account has been removed.");
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    // If we have a totpCode, this is the 2FA verification step
    if (totpCode) {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        mfa_token: totpCode.mfaToken,
        totp_code: totpCode.code,
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      return userData;
    }

    // Clear any stale auth state before login attempt
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];

    const response = await axios.post(`${API}/auth/login`, {
      email: email.trim().toLowerCase(),
      password,
    });

    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, mfa_token: response.data.mfa_token };
    }

    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const setSession = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, setSession, logout, loading, updateUser: setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
